import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_CONTROL_DETAIL_BY_ID,
  UPDATE_CONTROL_OWNER_BULK,
} from "../../../../apis/settingsApis/framework-apis/frameworkApis";
import { useEvidenceManagement } from "./Control-Hooks/useEvidenceManagement";
import { useFileUpload } from "./Control-Hooks/useFileUpload";
import { useControlPolling } from "./Control-Hooks/useControlPolling";
import UploadFileDialog from "../../../../components/UploadFileDialog/UploadFileDialog";
import {
  ChecksSection,
  ControlDetailsCard,
  EvidenceSection,
} from "./Control-Components/ControlComponents";
import { LegendCard } from "./Control-Components/LegendCard";
import UserSelectionDialog from "../../../../components/UserSelectionDialog";
const ITEMS_PER_PAGE = 5;

const ControlDetailDialog = ({ open, onClose, control, refetchData, onOwnerChanged }) => {
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);

  const [updateOwner, { loading: ownerSaving }] = useMutation(UPDATE_CONTROL_OWNER_BULK);

  const handleOwnerConfirm = async (user) => {
    if (!control?.controlsId) return;
    await updateOwner({
      variables: {
        controls_ids: [control.controlsId],
        control_owner_id: user?.id ?? null,
      },
    });
    refetchData?.();
    onOwnerChanged?.();
    setOwnerDialogOpen(false);
  };

  const { data, loading, refetch } = useQuery(GET_CONTROL_DETAIL_BY_ID, {
    variables: { controls_id: control?.controlsId },
    skip: !control?.controlsId,
  });

  const fileDetails = useMemo(() => {
    return data?.getControlsDetailByControlId?.fileDetails || [];
  }, [data?.getControlsDetailByControlId?.fileDetails]);

  const {
    currentPage,
    expandedEvidenceId,
    evidenceChecksLoading,
    paginatedData,
    totalItems,
    totalPages,
    handlePageChange,
    toggleExpand,
    getFileValidationStatus,
    renderValidationStatusChip,
    renderStatusDot,
    renderCheckNumbers,
    renderCheckDetails,
  } = useEvidenceManagement(control, fileDetails);

  const { isLoading, handleFileUpload } = useFileUpload(
    control,
    refetch,
    setIsUploadDialogOpen
  );
  useControlPolling(open, fileDetails, refetch);

  const handleDownloadDocument = (path, name) => {
    const link = document.createElement("a");
    link.href = `${process.env.REACT_APP_CLOUDFRONT_URL}/${path}`;
    link.download = `${name}`;
    link.click();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.25rem" }}>
        Control Details
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "grey.500",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {loading || isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <DialogContent dividers>
          <ControlDetailsCard
            control={control}
            onEditOwner={() => setOwnerDialogOpen(true)}
          />
          <LegendCard />
          <ChecksSection control={control} refetchData={refetchData} />
          <EvidenceSection
            totalItems={totalItems}
            paginatedData={paginatedData}
            currentPage={currentPage}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            expandedEvidenceId={expandedEvidenceId}
            toggleExpand={toggleExpand}
            evidenceChecksLoading={evidenceChecksLoading}
            handleDownloadDocument={handleDownloadDocument}
            getFileValidationStatus={getFileValidationStatus}
            renderValidationStatusChip={renderValidationStatusChip}
            renderStatusDot={renderStatusDot}
            renderCheckNumbers={renderCheckNumbers}
            renderCheckDetails={renderCheckDetails}
            setIsUploadDialogOpen={setIsUploadDialogOpen}
            ITEMS_PER_PAGE={ITEMS_PER_PAGE}
          />
        </DialogContent>
      )}

      <UserSelectionDialog
        open={ownerDialogOpen}
        onClose={() => setOwnerDialogOpen(false)}
        title="Change Control Owner"
        description="1 control will be updated."
        label="Control owner"
        currentUser={control?.controlOwnerData ?? null}
        onConfirm={handleOwnerConfirm}
        saving={ownerSaving}
        adminsOnly
        showUnassignOption
      />

      <UploadFileDialog
        open={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onFileUpload={handleFileUpload}
        allowedFileTypes={[
          ".jpeg",
          ".jpg",
          ".png",
          ".docx",
          ".doc",
          ".pdf",
          ".txt",
          ".zip",
          // removing .rar, .7z till the time they aren't tested
          // ".rar",
          // ".7z",
        ]}
        dialogTitle="Upload Evidence File"
        uploadButtonText="Upload"
      />
    </Dialog>
  );
};

export default ControlDetailDialog;
